import { useMemo, forwardRef } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { Line } from 'react-chartjs-2';
import {
  getChartColors,
  chartDefaults,
  formatCurrency,
  chartContainerStyle,
} from '../utils/chartConfig';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  annotationPlugin
);

/**
 * SavingsProjectionChart - 5-Year Cumulative Value Visualization
 *
 * Shows cumulative value over 5 years with:
 * - Teal area chart for cumulative value (energy savings + avoided costs)
 * - Horizontal dashed line for treatment cost (break-even reference)
 * - Vertical annotation at payback point
 * - Optional payment schedule overlay for 3-year payment mode
 *
 * Supports forwardRef for chart image export to PDF.
 *
 * @param {Object} projections - Portfolio projections data
 * @param {number} treatmentCost - Total treatment cost
 * @param {number} paybackMonths - Payback period in months (using total value)
 * @param {number} annualSavings - Annual energy savings amount (component 1)
 * @param {number} totalAnnualValue - Total annual value (all 9 components)
 * @param {string} paymentMode - 'upfront' or 'threeYear'
 * @param {Object} paymentMetrics - Payment metrics for 3-year mode
 * @param {React.Ref} ref - Optional ref forwarded to the Line chart for image export
 */
const SavingsProjectionChart = forwardRef(function SavingsProjectionChart({
  projections,
  treatmentCost,
  paybackMonths,
  annualSavings,
  totalAnnualValue = null, // NEW: Comprehensive value
  paymentMode = 'upfront',
  paymentMetrics = null,
}, ref) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const colors = getChartColors(theme.palette.mode);

  // Use total annual value if available, otherwise fall back to energy savings
  const displayValue = totalAnnualValue || annualSavings;
  const hasComprehensiveValue = totalAnnualValue && totalAnnualValue !== annualSavings;

  // Generate year-by-year data points
  const chartData = useMemo(() => {
    if (!projections || !displayValue) return null;

    // Calculate cumulative value for years 0-5 (using comprehensive value)
    const years = [0, 1, 2, 3, 4, 5];
    const cumulativeSavings = years.map((year) => year * displayValue);

    // Calculate cumulative payments based on mode
    // Round all values to avoid floating-point precision issues
    let cumulativePayments;
    if (paymentMode === 'threeYear') {
      // 3-year payment: 1/3 each year for 3 years
      const annualPayment = Math.round(treatmentCost / 3 * 100) / 100;
      cumulativePayments = years.map((year) => {
        if (year === 0) return 0;
        if (year <= 3) return Math.round(annualPayment * year * 100) / 100;
        return treatmentCost; // Fully paid after year 3
      });
    } else {
      // Upfront: Full cost at year 0
      cumulativePayments = years.map(() => treatmentCost);
    }

    // Calculate net position (savings - payments made so far)
    // Round to avoid floating-point issues
    const netPosition = cumulativeSavings.map((savings, i) =>
      Math.round((savings - cumulativePayments[i]) * 100) / 100
    );

    const datasets = [
      {
        label: hasComprehensiveValue ? 'Cumulative Value' : 'Cumulative Savings',
        data: cumulativeSavings,
        borderColor: colors.teal,
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 350);
          gradient.addColorStop(0, 'rgba(78, 205, 196, 0.3)');
          gradient.addColorStop(1, 'rgba(78, 205, 196, 0.02)');
          return gradient;
        },
        borderWidth: 3,
        fill: true,
        tension: 0.3,
        pointRadius: 6,
        pointHoverRadius: 8,
        pointBackgroundColor: colors.teal,
        pointBorderColor: colors.background,
        pointBorderWidth: 2,
      },
      {
        label: paymentMode === 'threeYear' ? 'Cumulative Payments' : 'Treatment Cost',
        data: cumulativePayments,
        borderColor: colors.orange,
        backgroundColor: 'transparent',
        borderWidth: 2,
        borderDash: paymentMode === 'threeYear' ? [] : [8, 4],
        fill: false,
        tension: 0,
        pointRadius: paymentMode === 'threeYear' ? 5 : 0,
        pointHoverRadius: paymentMode === 'threeYear' ? 7 : 0,
        pointBackgroundColor: colors.orange,
        pointBorderColor: colors.background,
        pointBorderWidth: 2,
        stepped: paymentMode === 'threeYear' ? 'after' : false,
      },
      {
        label: 'Net Position',
        data: netPosition,
        borderColor: colors.primary,
        backgroundColor: 'transparent',
        borderWidth: 2,
        borderDash: [5, 5],
        fill: false,
        tension: 0.3,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: colors.primary,
        pointBorderColor: colors.background,
        pointBorderWidth: 2,
      },
    ];

    return {
      labels: ['Start', 'Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5'],
      datasets,
    };
  }, [projections, displayValue, treatmentCost, paymentMode, hasComprehensiveValue]);

  // Chart options with payback annotation
  const options = useMemo(() => {
    const annualPayment = treatmentCost / 3;
    // Use comprehensive value for cash flow positive check
    const isCashPositiveFromYear1 = paymentMode === 'threeYear' && displayValue > annualPayment;

    // Build annotations based on payment mode
    const annotations = {};

    if (paymentMode === 'upfront') {
      // Upfront mode: Traditional payback annotation
      const paybackYear = paybackMonths / 12;
      annotations.paybackLine = {
        type: 'line',
        xMin: paybackYear,
        xMax: paybackYear,
        borderColor: colors.primaryLight,
        borderWidth: 2,
        borderDash: [4, 4],
        label: {
          display: true,
          content: `Payback: ${paybackMonths.toFixed(1)} mo`,
          position: 'start',
          backgroundColor: 'rgba(160, 206, 78, 0.9)',
          color: '#0A0A0A',
          font: {
            size: 11,
            weight: 600,
          },
          padding: { x: 8, y: 4 },
          borderRadius: 4,
          yAdjust: -20,
        },
      };

      // Horizontal treatment cost line
      annotations.treatmentCostLine = {
        type: 'line',
        yMin: treatmentCost,
        yMax: treatmentCost,
        borderColor: colors.orange,
        borderWidth: 2,
        borderDash: [8, 4],
        label: {
          display: true,
          content: `Treatment Cost: ${formatCurrency(treatmentCost)}`,
          position: 'end',
          backgroundColor: 'rgba(255, 107, 53, 0.9)',
          color: '#fff',
          font: {
            size: 11,
            weight: 600,
          },
          padding: { x: 8, y: 4 },
          borderRadius: 4,
        },
      };
    } else if (isCashPositiveFromYear1) {
      // 3-Year mode with cash-positive from Year 1: No traditional payback needed
      annotations.cashPositiveLabel = {
        type: 'label',
        xValue: 1,
        yValue: displayValue * 0.5, // Position in middle of Year 1 value
        content: ['Cash Positive', 'From Year 1'],
        backgroundColor: 'rgba(76, 175, 80, 0.9)',
        color: '#fff',
        font: {
          size: 11,
          weight: 600,
        },
        padding: { x: 10, y: 6 },
        borderRadius: 4,
      };
    }

    return {
      ...chartDefaults,
      plugins: {
        ...chartDefaults.plugins,
        legend: {
          ...chartDefaults.plugins.legend,
          display: true,
        },
        tooltip: {
          ...chartDefaults.plugins.tooltip,
          callbacks: {
            label: (context) => {
              const label = context.dataset.label || '';
              const value = formatCurrency(context.parsed.y);
              return `${label}: ${value}`;
            },
          },
        },
        annotation: { annotations },
        // Explicitly disable datalabels to prevent messy floating-point display
        datalabels: {
          display: false,
        },
      },
      scales: {
        ...chartDefaults.scales,
        y: {
          ...chartDefaults.scales.y,
          beginAtZero: true,
          ticks: {
            ...chartDefaults.scales.y.ticks,
            callback: (value) => formatCurrency(value),
          },
        },
      },
    };
  }, [treatmentCost, paybackMonths, paymentMode, displayValue]);

  if (!chartData) {
    return null;
  }

  // Calculate 5-year totals for summary (using comprehensive value)
  const year5Value = displayValue * 5;
  const year5Net = year5Value - treatmentCost;

  return (
    <Box sx={{ my: 4 }}>
      {/* Section Header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            letterSpacing: '-0.01em',
            mb: 0.5,
          }}
        >
          5-Year {hasComprehensiveValue ? 'Value' : 'Savings'} Projection
          {paymentMode === 'threeYear' && (
            <Typography
              component="span"
              sx={{
                ml: 2,
                fontSize: '0.75rem',
                fontWeight: 600,
                color: isDark ? colors.teal : 'text.primary',
                background: 'rgba(78, 205, 196, 0.15)',
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                verticalAlign: 'middle',
              }}
            >
              3-Year Payment
            </Typography>
          )}
          {hasComprehensiveValue && (
            <Typography
              component="span"
              sx={{
                ml: 1,
                fontSize: '0.7rem',
                fontWeight: 500,
                color: 'text.secondary',
              }}
            >
              (energy savings + avoided costs)
            </Typography>
          )}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {paymentMode === 'threeYear'
            ? `Cumulative ${hasComprehensiveValue ? 'value' : 'savings'} vs. payment schedule over 3 years`
            : `Cumulative ${hasComprehensiveValue ? 'value' : 'savings'} trajectory with payback milestone`}
        </Typography>
      </Box>

      {/* Chart Container */}
      <Box sx={chartContainerStyle}>
        <Line ref={ref} data={chartData} options={options} />
      </Box>

      {/* Summary Stats Below Chart */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-around',
          mt: 3,
          p: 2,
          borderRadius: 2,
          background: paymentMode === 'threeYear'
            ? 'rgba(78, 205, 196, 0.08)'
            : 'rgba(78, 205, 196, 0.08)',
          border: paymentMode === 'threeYear'
            ? '1px solid rgba(78, 205, 196, 0.2)'
            : '1px solid rgba(78, 205, 196, 0.2)',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            5-Year Gross {hasComprehensiveValue ? 'Value' : 'Savings'}
          </Typography>
          <Typography
            sx={{
              fontSize: '1.25rem',
              fontWeight: 700,
              color: isDark ? colors.teal : 'text.primary',
              fontFamily: '"JetBrains Mono", monospace',
            }}
          >
            {formatCurrency(year5Value)}
          </Typography>
          {hasComprehensiveValue && annualSavings && (
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.65rem', mt: 0.5 }}>
              ({formatCurrency(annualSavings * 5)} energy only)
            </Typography>
          )}
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {paymentMode === 'threeYear' ? 'Total Cost (No Interest)' : 'Treatment Cost'}
          </Typography>
          <Typography
            sx={{
              fontSize: '1.25rem',
              fontWeight: 700,
              color: colors.orange,
              fontFamily: '"JetBrains Mono", monospace',
            }}
          >
            {formatCurrency(treatmentCost)}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            5-Year Net Profit
          </Typography>
          <Typography
            sx={{
              fontSize: '1.25rem',
              fontWeight: 700,
              color: isDark ? colors.primary : 'text.primary',
              fontFamily: '"JetBrains Mono", monospace',
            }}
          >
            {formatCurrency(year5Net)}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              color: colors.grey,
              fontSize: '0.65rem',
              mt: 0.5,
            }}
          >
            Same for both options
          </Typography>
        </Box>
      </Box>
    </Box>
  );
});

export default SavingsProjectionChart;
